import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { EASE } from "@/lib/site";
import { goToContact } from "@/lib/scroll";

const TILES = [
  { key: "barbearias", label: "Barbearias e salões", idea: "Site com agendamento online", img: "/img/seg_barbearia.webp", span: "sm:col-span-2 lg:row-span-2" },
  { key: "restaurantes", label: "Restaurantes", idea: "Cardápio digital e pedidos", img: "/img/seg_restaurante.webp", span: "lg:row-span-2" },
  { key: "advogados", label: "Advogados e escritórios", idea: "Site institucional que transmite confiança", img: "/img/seg_advogados.webp", span: "" },
  { key: "clinicas", label: "Clínicas", idea: "Sistema de agenda e pacientes", img: "/img/seg_clinicas.webp", span: "" },
  { key: "lojas", label: "Lojas e e-commerce", idea: "Loja virtual completa", img: "/img/seg_lojas.webp", span: "sm:col-span-2" },
  { key: "criadores", label: "Criadores de conteúdo", idea: "Landing page para lançamentos", img: "/img/seg_criadores.webp", span: "" },
];

const MORE = ["Profissionais autônomos", "Prestadores de serviços", "Profissionais liberais", "Personal trainers", "Corretores", "Contadores", "Infoprodutores", "Pequenas empresas", "Empresas de médio porte", "Empresas maiores", "Empreendedores em geral"];

const Tile = ({ t, i }) => (
  <motion.div
    data-testid={`segment-${t.key}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: EASE }}
    className={`group relative min-h-[220px] overflow-hidden rounded-3xl border border-white/[0.07] ${t.span}`}
  >
    <img src={t.img} alt={t.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover grayscale-[70%] transition-[transform,filter] duration-1000 ease-out group-hover:scale-[1.06] group-hover:grayscale-0" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
    <div className="absolute inset-0 bg-ws-lime/0 mix-blend-color transition-colors duration-700 group-hover:bg-ws-lime/10" />
    <div className="relative flex h-full flex-col justify-end p-5 sm:p-6">
      <span className="mb-auto inline-flex w-fit items-center gap-1.5 rounded-full border border-white/15 bg-black/40 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ws-text backdrop-blur-md">
        <span className="h-1.5 w-1.5 rounded-full bg-ws-lime" /> Solução sob medida
      </span>
      <h3 className="text-lg font-medium text-white sm:text-xl">{t.label}</h3>
      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ws-glow">
        Ex.: {t.idea}
        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </p>
    </div>
  </motion.div>
);

const MoreTile = () => (
  <motion.button
    data-testid="segment-outros"
    onClick={() => goToContact("Outro")}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
    className="group relative flex min-h-[220px] flex-col justify-between overflow-hidden rounded-3xl bg-ws-lime p-6 text-left text-ws-bg"
  >
    <span className="grid h-12 w-12 place-items-center rounded-full bg-ws-bg text-ws-lime transition-transform duration-500 group-hover:rotate-90">
      <Plus size={22} />
    </span>
    <div>
      <h3 className="text-xl font-semibold">E outros negócios</h3>
      <p className="mt-1.5 text-sm font-medium text-ws-bg/75">Seu segmento não está aqui? Conte para a gente.</p>
    </div>
  </motion.button>
);

export const Audiences = () => (
  <section id="para-quem" data-testid="audiences-section" className="relative py-24 sm:py-32">
    <div className="pointer-events-none absolute right-0 top-40 h-[400px] w-[400px] rounded-full bg-ws-lime/[0.05] blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading index="03" eyebrow="Para quem criamos" title="Para quem criamos?" sub="Não importa o tamanho do seu negócio. Se você precisa de tecnologia, podemos criar." testid="audiences-title" />
      <div className="mt-16 grid auto-rows-[220px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {TILES.map((t, i) => (
          <Tile key={t.key} t={t} i={i} />
        ))}
        <MoreTile />
      </div>
      <div className="mt-10 flex flex-wrap gap-2.5" data-testid="segments-more">
        {MORE.map((m, i) => (
          <motion.span
            key={m}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
            className="cursor-default rounded-full border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-ws-muted transition-colors duration-300 hover:border-ws-lime/50 hover:text-ws-text"
          >
            {m}
          </motion.span>
        ))}
      </div>
    </div>
  </section>
);
